import { css } from '@emotion/core';
import { Button, Chip, Theme, Tooltip, Typography, useTheme } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import * as React from 'react';

import { getKeywordSetsHighlightTerms } from '../../common/searches';
import { useAgent } from '../../hooks/use-agent';
import { useSearchResults } from '../../hooks/use-agent-search-draft';
import { useSlides } from '../../hooks/use-candidate-slides';
import { useHighlight } from '../../hooks/use-highlight';
import { usePageDialog } from '../../hooks/use-page-dialog';
import { useSession } from '../../hooks/use-session';
import { formatCountsSummary } from '../../utils/format-search-counts';
import { ProfilePreviewSkeleton } from '../profile-preview-skeleton';
import { SearchStrategyDisplay } from '../search-strategy-display';
import { AgentResultsHeader } from './agent-results-header';
import { SearchResultsList } from './search-results-list';

const styles = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: hidden;

    .job-search-results-container {
        flex: 1 1 auto;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background: #f4f6f8;
    }

    .job-search-side-by-side {
        overflow: hidden;
    }

    .strategy-panel {
        max-width: 768px;
        border: 1px solid ${theme.palette.divider};
        background: ${theme.palette.background.paper};
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .strategy-panel__title {
            font-weight: 600;
            font-size: 16px;
            padding: 5px 16px;
            border-bottom: 1px solid ${theme.palette.divider};
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            gap: 10px;
            height: 40px;

            .preview-chip {
                height: 22px;
                font-size: 11px;
                font-weight: 500;
                background: #fff3e0;
                color: #e65100;
            }
        }

        .strategy-panel__content {
            flex: 1 1 auto;
            overflow-y: auto;
        }

        .strategy-panel__footer {
            padding: 10px 16px;
            height: 40px;
            border-top: 1px solid ${theme.palette.divider};
            display: flex;
            justify-content: flex-end;
            flex: 0 0 auto;
        }
    }

    .job-search-results {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid ${theme.palette.divider};
        background: ${theme.palette.background.paper};

        .search-results {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            overflow: hidden;
        }

        .search-results-header {
            padding: 5px 16px;
            border-bottom: 1px solid ${theme.palette.divider};
            flex: 0 0 auto;

            .search-stats {
                font-weight: 500;
            }
        }

        .search-results-content {
            flex: 1 1 auto;
            overflow-y: auto;
            padding: 16px;
        }

        .search-results-footer {
            padding: 10px 16px;
            border-top: 1px solid ${theme.palette.divider};
            display: flex;
            justify-content: center;
            flex: 0 0 auto;
        }
    }
`;

export const AgentSearchPreview: React.FC = () => {
    const theme = useTheme<Theme>();
    const { setList } = useSlides();
    const { onClose } = usePageDialog();
    const { onOpen } = useAgent();
    const { user } = useSession();
    const { pushHighlightTerms } = useHighlight();
    const [hasNavigatedAfterSave, setHasNavigatedAfterSave] = React.useState(false);

    const {
        aggregateStats,
        counts,
        keywordSets,
        profiles,
        strategy,
        loading,
        error,
        currentPage,
        totalPages,
        handleGoToPage,
        handleSave,
        resultsExpired,
        saving,
        title,
        searchId,
        userId
    } = useSearchResults();

    const isOwner = user?.id === userId;

    React.useEffect(() => {
        if (keywordSets) {
            return pushHighlightTerms(getKeywordSetsHighlightTerms(keywordSets));
        }
    }, [keywordSets, pushHighlightTerms]);

    const handleProfileClick = (personId: string) => {
        const list = profiles.map((p) => ({ personId: p.personId, selectedTab: 'profile' }));
        setList(list, { personId, selectedTab: 'profile' });
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        handleGoToPage(page);
    };

    const handleClose = () => {
        onClose();
        onOpen();
    };

    React.useEffect(() => {
        if (searchId && !hasNavigatedAfterSave) {
            setHasNavigatedAfterSave(true);
            // Close the preview when the search is saved and open the agent panel
            onClose();
            onOpen();
        }
    }, [searchId, hasNavigatedAfterSave, onClose, onOpen]);

    const hasProfiles = profiles.length > 0 && !resultsExpired;

    const saveDisabled = !!searchId || saving || !isOwner;
    const saveTooltip = searchId
        ? 'Search already saved'
        : !isOwner
          ? 'Only the owner can save this search'
          : saving
            ? 'Saving...'
            : 'Save this search';
    const saveButton = strategy ? (
        <Tooltip title={saveTooltip}>
            <span>
                <Button disabled={saveDisabled} onClick={handleSave}>
                    {saving ? 'Saving...' : 'Save Search'}
                </Button>
            </span>
        </Tooltip>
    ) : null;

    const strategyPanel = strategy ? (
        <div className="strategy-panel">
            <div className="strategy-panel__title">
                <Chip label="Preview" size="small" className="preview-chip" />
                {title}
            </div>
            <div className="strategy-panel__content">
                <SearchStrategyDisplay strategy={strategy} aggregateStats={aggregateStats} keywordSets={keywordSets} />
            </div>
            <div className="strategy-panel__footer">
                <Button onClick={handleClose}>Close</Button>
                {saveButton}
            </div>
        </div>
    ) : null;

    // Right panel content
    let resultsContent: React.ReactNode;
    if (loading && profiles.length === 0 && !resultsExpired) {
        resultsContent = <ProfilePreviewSkeleton />;
    } else if (error) {
        resultsContent = <div className="search-error">{error.message || 'Failed to load search results'}</div>;
    } else if (profiles.length === 0 && !resultsExpired) {
        resultsContent = <Typography variant="body2">No profiles found</Typography>;
    } else {
        resultsContent = <SearchResultsList onProfileClick={handleProfileClick} />;
    }

    const pagination =
        hasProfiles && totalPages > 1 ? (
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
        ) : null;

    return (
        <div css={styles(theme)}>
            <AgentResultsHeader title="Search Results Preview" />
            <div className="job-search-results-container">
                <div className="job-search-side-by-side">
                    {strategyPanel}
                    <div className="job-search-results">
                        <div className="search-results">
                            <div className="search-results-header">
                                <div className="search-stats">{formatCountsSummary(counts)}</div>
                            </div>
                            <div className="search-results-content">{resultsContent}</div>
                            <div className="search-results-footer">{pagination}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
